import axios, { AxiosError, AxiosResponse } from 'axios';
import { loginRequest } from '../../authConfig';

const axiosInstance = axios.create({
	timeout: 60000,
	headers: {
		'Content-Type': 'application/json',
	},
});

// Simple initialization function
export function initializeAxiosInterceptors(msalInstance: any): void {
	console.log('🔧 Initializing axios interceptors...');

	// Request interceptor - add auth token
	axiosInstance.interceptors.request.use(
		async (config) => {
			try {
				const accounts = msalInstance?.getAllAccounts();
				
				if (accounts && accounts.length > 0) {
					try {
						const response = await msalInstance.acquireTokenSilent({
							scopes: loginRequest.scopes,
							account: accounts[0],
						});
						config.headers.Authorization = `Bearer ${response.accessToken}`;
					} catch (error: any) {
						console.log('🔑 Token acquisition failed in interceptor:', error);
						
						// Handle InteractionRequiredAuthError by triggering interactive login
						if (error.name === 'InteractionRequiredAuthError' || error.errorCode === 'interaction_required') {
							console.log('🔑 [INTERCEPTOR] Interaction required - triggering interactive login');
							try {
								await msalInstance.acquireTokenRedirect({
									scopes: loginRequest.scopes,
									account: accounts[0],
								});
								// User will be redirected to login and back with fresh token
								return Promise.reject(new Error('Redirecting to login'));
							} catch (redirectError) {
								console.log('🔑 [INTERCEPTOR] Interactive login failed:', redirectError);
							}
						}
						
						// For other errors, redirect to login
						console.log('🔑 [INTERCEPTOR] Token acquisition failed - redirecting to login');
						window.location.href = '/login';
						return Promise.reject(new Error('Token acquisition failed'));
					}
				} else {
					console.log('🔑 No accounts found in interceptor');
					// No accounts found - redirect to login
					console.log('🔑 [INTERCEPTOR] No accounts found - redirecting to login');
					window.location.href = '/login';
					return Promise.reject(new Error('No accounts found'));
				}
			} catch (error) {
				console.log('🔑 Error in request interceptor:', error);
				return Promise.reject(error);
			}
			return config;
		},
		(error) => Promise.reject(error)
	);

	// Response interceptor - handle 401 errors
	axiosInstance.interceptors.response.use(
		(response: AxiosResponse) => response,
		async (error: AxiosError) => {
			if (error.response?.status === 401) {
				console.log('🔐 401 error detected - triggering interactive login');
				
				// Try to trigger interactive login for 401 errors
				try {
					const accounts = msalInstance?.getAllAccounts();
					if (accounts && accounts.length > 0) {
						console.log('🔑 [RESPONSE INTERCEPTOR] Triggering interactive login for 401 error');
						await msalInstance.acquireTokenRedirect({
							scopes: loginRequest.scopes,
							account: accounts[0],
						});
						// User will be redirected to login and back with fresh token
						return Promise.reject(new Error('Redirecting to login'));
					}
				} catch (redirectError) {
					console.log('🔑 [RESPONSE INTERCEPTOR] Interactive login failed:', redirectError);
					// If interactive login fails, redirect to login
					console.log('🔑 [RESPONSE INTERCEPTOR] Redirecting to login due to interactive login failure');
					window.location.href = '/login';
				}
				
				// Don't redirect - let MSAL handle the authentication flow
				// The component will handle the 401 error naturally
			}
			return Promise.reject(error);
		}
	);

	console.log('✅ Axios interceptors initialized');
}

export default axiosInstance;